import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Autocomplete,
} from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { Cancel, Delete, SwapHoriz } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import ApiService from "../../services/categoryManagementApi";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import { dealerSchema } from "../../utils/Schema";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import axios from "axios";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const { dealerManagementApi } = ApiService;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const GOOGLE_API_KEY = "AIzaSyDsC3bmbBV7x9bIFL1KvwMi_Yk8-hL16Y8";

function AddDealer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const documentInputRef = useRef(null);
  const [data, setData] = useState(null);
  const [deletedDocuments, setDeletedDocuments] = useState([]);
  const [oldDocuments, setOldDocuments] = useState([]);
  const [phoneUpdate, setPhoneUpdate] = useState("");
  const [latLng, setLatLng] = useState({ lat: "", lng: "" });
  const [commissionType, setCommissionType] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [commissionInputType, setCommissionInputType] = useState({});
  const [availableItems, setAvailableItems] = useState([]);
  const [dealerCommissionInputType, setDealerCommissionInputType] = useState("percentage");

  const addressRefs = {
    houseNumber: useRef(null),
    streetNumber: useRef(null),
    city: useRef(null),
    village: useRef(null),
    district: useRef(null),
    commune: useRef(null),
    postalCode: useRef(null),
  };

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        initializeAutocomplete();
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initializeAutocomplete;
      document.body.appendChild(script);
    };

    const initializeAutocomplete = () => {
      Object.keys(addressRefs).forEach((field) => attachAutocomplete(field));
    };

    const attachAutocomplete = (fieldName) => {
      if (!addressRefs[fieldName]?.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(
        addressRefs[fieldName].current,
        { types: ["geocode"] }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const allFields = {
          houseNumber: "",
          city: "",
          village: "",
          district: "",
          commune: "",
          streetNumber: "",
          postalCode: ""
        };

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number") || types.includes("sublocality_level_3")) {
            allFields.houseNumber = component.long_name;
          }
          if (types.includes("route") || types.includes("sublocality_level_2") || types.includes("locality")) {
            allFields.streetNumber = component.long_name;
          }
          if (types.includes("sublocality") || types.includes("sublocality_level_1") || types.includes("neighborhood")) {
            allFields.village = component.long_name;
          }
          if (types.includes("administrative_area_level_2")) {
            allFields.district = component.long_name;
          }
          if (types.includes("administrative_area_level_3")) {
            allFields.commune = component.long_name;
          }
          if (types.includes("administrative_area_level_1")) {
            allFields.city = component.long_name;
          }
          if (types.includes("postal_code")) {
            allFields.postalCode = component.long_name;
          }
        });

        formik.setValues((prev) => ({
          ...prev,
          ...allFields,
        }));

        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          setLatLng({ lat, lng });

          formik.setFieldValue("latitude", lat);
          formik.setFieldValue("longitude", lng);
        }
      });
    };

    loadGoogleMapsScript();
  }, []);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await dealerManagementApi.getById({ id });
        const resData = response?.data?.data?.result;
        const setting = response?.data?.data?.dealerCommissionSetting;

        setSelectedDocuments(resData?.documents || []);
        setOldDocuments(resData?.documents || []);
        setData(resData);

        if (resData?.phoneNumber && resData?.countryCode) {
          setPhoneUpdate(`${resData?.countryCode}${resData?.phoneNumber}`);
        }

        if (setting?.commissionType) {
          setCommissionType(setting.commissionType);

          if (["product", "category"].includes(setting.commissionType)) {
            const dropdownResponse = await dealerManagementApi.allDropDownCategory({
              commissionType: setting.commissionType,
            });

            const items = dropdownResponse?.data?.data?.result || [];
            setAvailableItems(items);

            let selectedRawItems = [];
            if (setting.commissionType === "product") {
              selectedRawItems = setting.product || [];
            } else if (setting.commissionType === "category") {
              selectedRawItems = setting.categories || [];
            }

            const selectedMapped = selectedRawItems.map((item) => {
              const id =
                setting.commissionType === "product"
                  ? item.productId
                  : item.categoryId || item._id;

              const matched = items.find((i) => i._id === id);

              setCommissionInputType(prev => ({
                ...prev,
                [id]: item.amount ? 'amount' : 'percentage'
              }));

              return {
                productId: id,
                name: matched?.name || `(${id.slice(-4)})`,
                percentage: item.percentage || '',
                amount: item.amount || ''
              };
            });


            setSelectedItems(selectedMapped);
          }


          if (setting.commissionType === "dealer") {
            if (setting.dealerCommission !== null && setting.dealerCommission !== undefined) {
              setDealerCommissionInputType("percentage");
              formik.setFieldValue("commission", setting.dealerCommission);
            } else if (setting.dealerCommissionAmount !== null && setting.dealerCommissionAmount !== undefined) {
              setDealerCommissionInputType("amount");
              formik.setFieldValue("commission", setting.dealerCommissionAmount);
            } else {
              formik.setFieldValue("commission", "");
              setDealerCommissionInputType("percentage");
            }
          }

        }

      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);


  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  useEffect(() => {
    if (commissionType && availableItems.length && selectedItems.length) {
      const updatedSelected = selectedItems.map((item) => {
        const match = availableItems.find((a) => a._id === item.productId);
        return { ...item, name: match?.name || item.name };
      });
      setSelectedItems(updatedSelected);
    }
  }, [availableItems, commissionType]);


  const getFullAddress = () => {
    const { houseNumber, streetNumber, district, city, village, commune, postalCode } =
      formik.values;

    const fullAddress = `${houseNumber || ""} ${streetNumber || ""} ${district || ""} ${city || ""
      } ${village || ""} ${commune || ""} ${postalCode || ""}`.trim();

    return fullAddress;
  };

  const getLatLngFromAddress = async (address) => {
    try {
      let lat = "";
      let lng = "";
      const response = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: {
            address,
            key: GOOGLE_API_KEY,
          },
        }
      );
      const location = response?.data?.results?.[0]?.geometry?.location;

      if (location) {
        lat = location?.lat;
        lng = location?.lng;
      }
      if (response?.status == 200) {
        return { lat, lng };
      } else {
        throw new Error("Geocoding failed: " + data.status);
      }
    } catch (error) {
      console.error("Error fetching geocode:", error);
      return null;
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      businessName: data?.businessName || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      countryCode: data?.countryCode || "",
      houseNumber: data?.houseNumber || "",
      streetNumber: data?.streetNumber || "",
      village: data?.village || "",
      district: data?.district || "",
      commune: data?.commune || "",
      city: data?.city || "",
      latitude: data?.location?.coordinates[1] || "",
      longitude: data?.location?.coordinates[0] || "",
      postalCode: data?.postalCode || "",
      imagePath: "dealer",
      documents: data?.documents || [],
      dealerId: data?.dealerId || "",
      dealerOrderRange: data?.dealerOrderRange || "",
      commission: data?.commission || "",
    },
    validationSchema: dealerSchema(id),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let { phoneNumber, countryCode } = values;

        if (!countryCode.startsWith("+")) {
          countryCode = `+${countryCode}`;
        }

        if (phoneNumber.startsWith(countryCode.replace("+", ""))) {
          phoneNumber = phoneNumber
            .replace(countryCode.replace("+", ""), "")
            .trim();
        }

        if (phoneNumber.startsWith("0")) {
          phoneNumber = phoneNumber.slice(1);
        }

        const fullAddress = getFullAddress();
        const latlong = await getLatLngFromAddress(fullAddress);

        if ((!latLng.lat || !latLng.lng) && (!latlong?.lat || !latlong?.lng)) {
          toast.error("Failed to retrieve location.");
          setLoading(false);
          return;
        }
        setLatLng(latlong);

        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("businessName", values.businessName);
        formData.append("email", values.email);
        formData.append("phoneNumber", phoneNumber);
        formData.append("countryCode", values.countryCode);
        formData.append("imagePath", "dealer");
        formData.append("dealerId", values.dealerId);
        formData.append("houseNumber", values.houseNumber);
        formData.append("streetNumber", values.streetNumber);
        formData.append("district", values.district);
        formData.append("city", values.city);
        formData.append("village", values.village);
        formData.append("commune", values.commune);
        formData.append("postalCode", values.postalCode);
        formData.append("dealerOrderRange", values.dealerOrderRange);
        formData.append("lat", latLng.lat || latlong.lat || values.latitude);
        formData.append("lng", latLng.lng || latlong.lng || values.longitude);

        formData.append("commissionType", commissionType);

        if (commissionType === "dealer") {
          if (dealerCommissionInputType === "percentage") {
            formData.append("dealerCommission", values.commission);
          } else {
            formData.append("dealerCommissionAmount", values.commission);
          }
        }

        if (commissionType === "product") {
          const productData = selectedItems.map(item => ({
            productId: item.productId,
            percentage: commissionInputType[item.productId] === 'percentage' ? item.percentage : null,
            amount: commissionInputType[item.productId] === 'amount' ? item.amount : null
          }));
          formData.append("product", JSON.stringify(productData));
        }

        if (commissionType === "category") {
          formData.append("categories", JSON.stringify(selectedItems));
        }




        const newDocuments = selectedDocuments.filter(
          (doc) =>
            !oldDocuments.some(
              (oldDoc) => oldDoc.name === doc.name && oldDoc.size === doc.size
            )
        );

        if (newDocuments.length > 0) {
          newDocuments.forEach((doc) => {
            formData.append("documents", doc);
          });
        }
        formData.append("oldDocuments", JSON.stringify(oldDocuments));
        if (deletedDocuments.length > 0) {
          formData.append("deletedDocuments", JSON.stringify(deletedDocuments));
        }
        const response = id
          ? await dealerManagementApi.updateDealer(formData)
          : await dealerManagementApi.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (formik.values.latitude && formik.values.longitude) {
        setLatLng({
          lat: formik.values.latitude,
          lng: formik.values.longitude,
        });
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [formik.values.latitude, formik.values.longitude]);

  const handleCommissionInputTypeChange = (productId, type) => {
    setCommissionInputType(prev => ({
      ...prev,
      [productId]: type
    }));

    // Reset both values when switching type
    setSelectedItems(prev =>
      prev.map(item =>
        item.productId === productId
          ? { ...item, percentage: '', amount: '' }
          : item
      )
    );
  };

  const handleCommissionValueChange = (productId, value, type) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.productId === productId
          ? { ...item, [type]: value }
          : item
      )
    );
  };

  const handleCommissionTypeChange = async (e) => {
    const type = e.target.value;
    setCommissionType(type);
    setSelectedItems([]);
    setCommissionInputType({});

    if (type === "product" || type === "category") {
      try {
        const response = await dealerManagementApi.allDropDownCategory({ commissionType: type });
        setAvailableItems(response?.data?.data?.result || []);
      } catch (err) {
        errorHandler(err);
      }
    }
  };

  const handleDealerCommissionInputTypeChange = () => {
    setDealerCommissionInputType((prev) => (prev === "percentage" ? "amount" : "percentage"));
    formik.setFieldValue("commission", "");
  };

  const handleDocumentChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    let newFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (file.type !== "application/pdf") {
        invalidFiles.push(file.name);
      } else if (
        !selectedDocuments.some(
          (existingFile) =>
            existingFile.name === file.name && existingFile.size === file.size
        )
      ) {
        newFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(`Only PDF allowed.`);
    }

    if (newFiles.length > 0) {
      setSelectedDocuments((prevState) => [...prevState, ...newFiles]);
      formik.setFieldValue("documents", [
        ...formik.values.documents,
        ...newFiles,
      ]);
    } else if (invalidFiles.length === 0) {
      toast.error("You cannot select the same file again.");
    }

    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  const handleDeleteDocument = (index) => {
    const deletedDoc = selectedDocuments[index];

    setDeletedDocuments((prevState) => [...prevState, deletedDoc]);

    const updatedDocuments = selectedDocuments.filter((_, i) => i !== index);
    setSelectedDocuments(updatedDocuments);

    setOldDocuments(updatedDocuments);

    formik.setFieldValue("documents", updatedDocuments);

    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Dealer</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Dealer`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={6} mt={2}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={formik.values.businessName}
                    onChange={formik.handleChange}
                    name="businessName"
                    error={Boolean(
                      formik.touched.businessName && formik.errors.businessName
                    )}
                    helperText={
                      formik.touched.businessName && formik.errors.businessName
                    }
                  />
                </Grid>
                <Grid item xs={6} mt={2}>
                  <TextField
                    fullWidth
                    label="Email (optional)"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    name="email"
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                  />
                </Grid>
                <Grid item xs={6} mt={2}>
                  <PhoneInput
                    country={"kh"}
                    value={phoneUpdate || formik.values.phoneNumber}
                    onChange={(phone, country) => {
                      setPhoneUpdate(phone);
                      formik.setFieldValue("phoneNumber", phone);
                      formik.setFieldValue(
                        "countryCode",
                        `+${country.dialCode}`
                      );
                    }}
                    inputProps={{
                      name: "phoneNumber",
                      required: true,
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                    }}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginLeft: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formik.errors.phoneNumber}
                    </div>
                  )}
                </Grid>
                <Grid item xs={6} mt={2}>
                  <TextField
                    fullWidth
                    label="Dealer Id"
                    disabled={Boolean(id)}
                    value={formik.values.dealerId}
                    onChange={(e) => {
                      const value = e.target.value;
                        formik.setFieldValue("dealerId", value);
                      
                    }}
                    type="text"
                    name="dealerId"
                    required={!id}
                    error={Boolean(
                      formik.touched.dealerId && formik.errors.dealerId
                    )}
                    helperText={
                      formik.touched.dealerId && formik.errors.dealerId
                    }
                  />
                </Grid>


                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="House No."
                    required
                    inputRef={addressRefs.houseNumber}
                    value={formik.values.houseNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="houseNumber"
                    error={Boolean(
                      formik.touched.houseNumber && formik.errors.houseNumber
                    )}
                    helperText={
                      formik.touched.houseNumber && formik.errors.houseNumber
                    }
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Street No."
                    required
                    value={formik.values.streetNumber}
                    onChange={formik.handleChange}
                    name="streetNumber"
                    error={Boolean(formik.touched.streetNumber && formik.errors.streetNumber)}
                    helperText={formik.touched.streetNumber && formik.errors.streetNumber}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="District"
                    required
                    value={formik.values.district}
                    onChange={formik.handleChange}
                    name="district"
                    error={Boolean(
                      formik.touched.district && formik.errors.district
                    )}
                    helperText={
                      formik.touched.district && formik.errors.district
                    }
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Commune"
                    required
                    value={formik.values.commune}
                    onChange={formik.handleChange}
                    name="commune"
                    error={Boolean(formik.touched.commune && formik.errors.commune)}
                    helperText={formik.touched.commune && formik.errors.commune}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Village"
                    value={formik.values.village}
                    onChange={formik.handleChange}
                    name="village"
                    error={Boolean(
                      formik.touched.village && formik.errors.village
                    )}
                    helperText={formik.touched.village && formik.errors.village}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City/Province"
                    required
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    name="city"
                    error={Boolean(formik.touched.city && formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    required
                    value={formik.values.latitude}
                    onChange={(e) => formik.setFieldValue("latitude", e.target.value)}
                    name="latitude"
                    error={Boolean(formik.touched.latitude && formik.errors.latitude)}
                    helperText={formik.touched.latitude && formik.errors.latitude}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    required
                    value={formik.values.longitude}
                    onChange={(e) => formik.setFieldValue("longitude", e.target.value)}
                    name="longitude"
                    error={Boolean(formik.touched.longitude && formik.errors.longitude)}
                    helperText={formik.touched.longitude && formik.errors.longitude}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={formik.values.postalCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,6}$/.test(value)) {
                        formik.setFieldValue("postalCode", value);
                      } else {
                        e.preventDefault();
                      }
                    }}
                    name="postalCode"
                    type="text"
                    error={Boolean(
                      formik.touched.postalCode && formik.errors.postalCode
                    )}
                    helperText={formik.touched.postalCode && formik.errors.postalCode}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="commission-type-label">Commission Type</InputLabel>
                    <Select
                      labelId="commission-type-label"
                      value={commissionType}
                      onChange={handleCommissionTypeChange}
                      label="Commission Type"
                    >
                      <MenuItem value="dealer">Dealer</MenuItem>
                      <MenuItem value="product">Product</MenuItem>
                      {/* <MenuItem value="category">Category</MenuItem> */}
                    </Select>
                  </FormControl>
                </Grid>

                {commissionType === "dealer" && (
                  <Grid item xs={6}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs>
                        <TextField
                          fullWidth
                          name="commission"
                          label={`Dealer Commission (${dealerCommissionInputType === "percentage" ? "%" : "Amount"}) *`}
                          {...formik.getFieldProps("commission")}
                          error={Boolean(formik.touched.commission && formik.errors.commission)}
                          helperText={formik.touched.commission && formik.errors.commission}
                          inputProps={{
                            pattern: "^[0-9]*([.][0-9]{0,2})?$",
                            inputMode: "decimal",
                            maxLength: 6,
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={handleDealerCommissionInputTypeChange}
                          startIcon={<SwapHoriz />}
                        >
                          {dealerCommissionInputType === "percentage" ? "Amount" : "Percentage"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}


                {(commissionType === "product" || commissionType === "category") && (
                  <Grid item xs={12} mt={2}>
                    <Autocomplete
                      multiple
                      options={availableItems}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      value={availableItems.filter(item =>
                        selectedItems.some(sel =>
                          sel.productId === item._id || sel.categoryId === item._id
                        )
                      )}

                      onChange={(event, newValue) => {
                        const updated = newValue.map((item) => {
                          const existing = selectedItems.find(
                            (i) => i.productId === item._id || i.categoryId === item._id
                          );

                          if (existing) return existing;

                          const newItem =
                            commissionType === "product"
                              ? { productId: item._id, name: item.name, percentage: "", amount: "" }
                              : { categoryId: item._id, name: item.name, percentage: "" };

                          return newItem;
                        });

                        const newInputType = { ...commissionInputType };
                        newValue.forEach((item) => {
                          const id = item._id;
                          if (!(id in newInputType)) {
                            newInputType[id] = "percentage";
                          }
                        });

                        setCommissionInputType(newInputType);
                        setSelectedItems(updated);
                      }}


                      renderTags={() => null}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Select ${commissionType}s`}
                          placeholder="Search..."
                        />
                      )}
                    />
                  </Grid>
                )}

                {selectedItems.map((item, index) => (
                  <Grid item xs={6}>

                    <Box key={item.productId} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {item.name}
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            label={commissionInputType[item.productId] === 'percentage' ? 'Percentage' : 'Amount'}
                            value={commissionInputType[item.productId] === 'percentage' ? item.percentage : item.amount}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (commissionInputType[item.productId] === 'percentage') {
                                if (/^\d*\.?\d*$/.test(value) && parseFloat(value) <= 100) {
                                  handleCommissionValueChange(item.productId, value, 'percentage');
                                }
                              } else {
                                if (/^\d*\.?\d*$/.test(value)) {
                                  handleCommissionValueChange(item.productId, value, 'amount');
                                }
                              }
                            }}
                            type="text"
                            InputProps={{
                              endAdornment: commissionInputType[item.productId] === 'percentage' ? '%' : '$'
                            }}
                          />
                        </Grid>
                        <Grid item>
                          <Button
                            variant="outlined"
                            onClick={() => handleCommissionInputTypeChange(
                              item.productId,
                              commissionInputType[item.productId] === 'percentage' ? 'amount' : 'percentage'
                            )}
                            startIcon={<SwapHoriz />}
                          >
                            Switch to {commissionInputType[item.productId] === 'percentage' ? 'Amount' : 'Percentage'}
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}



                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="dealerOrderRange"
                    label="Dealer Order Range (in meters) *"
                    {...formik.getFieldProps("dealerOrderRange")}
                    error={Boolean(
                      formik.touched.dealerOrderRange &&
                      formik.errors.dealerOrderRange
                    )}
                    helperText={
                      formik.touched.dealerOrderRange &&
                      formik.errors.dealerOrderRange
                    }
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 15,
                    }}
                    onKeyDown={(e) => {
                      if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight"
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6} mt={2}>
                  <Box display="flex" alignItems="center">
                    <Button variant="outlined" component="label">
                      Upload Documents
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="application/pdf"
                        onChange={handleDocumentChange}
                        ref={documentInputRef}
                      />
                    </Button>
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {selectedDocuments.length > 0
                        ? `${selectedDocuments.length} File${selectedDocuments.length > 1 ? "s" : ""
                        } Selected`
                        : "No file selected"}
                    </Typography>
                  </Box>

                  {selectedDocuments.length > 0 && (
                    <Box mt={2}>
                      {selectedDocuments.map((document, index) => (
                        <Box
                          key={index}
                          position="relative"
                          display="inline-block"
                          mr={2}
                          mb={2}
                        >
                          <Link
                            to={document.preview || IMAGE_BASE + document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src="/assets/pdf.png"
                              alt="PDF Preview"
                              width={80}
                              height={80}
                            />
                          </Link>
                          <Button
                            variant="text"
                            color="error"
                            className="deletepdficon"
                            onClick={() => handleDeleteDocument(index)}
                            startIcon={<Cancel />}
                            sx={{
                              position: "absolute",
                              left: "49px",
                              top: "-15px",
                              backgroundColor: "rgba(255, 255, 255, 0.7)",
                              borderRadius: "50%",
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} mt={4}>
                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    loading={loading}
                  >
                    {id ? "Edit" : "Create"} Dealer
                  </LoadingButton>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddDealer;
